import { Component, OnInit } from '@angular/core';
import { Auth, signInWithEmailLink, authState } from '@angular/fire/auth';
import { Router, NavigationEnd } from '@angular/router';
import { filter } from 'rxjs/operators';
import { getAuth, GoogleAuthProvider } from 'firebase/auth';
import { map } from 'rxjs';
import { AuthService } from './auth/auth.service';
import { initFlowbite } from 'flowbite';
import { HttpClient } from '@angular/common/http';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
declare var $: any; // Declara $ para usar jQuery dentro de este archivo.
import { ApiService } from './services/api.service';
import { PermissionsService } from './services/permissions.service';
import { TwoFactorAuthService } from './services/two-factor-auth.service';
import { TrackingService } from './services/tracking.service';

@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.css']
})
export class AppComponent implements OnInit {
  title = 'meetzy-dashboard';
  email: string = "";
  loading: boolean = true;
  partner={
    is: false,
    image: "",
    id: ""
  }

  isLoggedIn = false;
  isLoginRoute: boolean = false;
  isGeneratorRoute: boolean = false;
  isFullWidthRoute: boolean = false;
  isAdvancedEditorRoute: boolean = false;
  mobileMenuOpen: boolean = false;
  
  // Dark mode state
  private _isDarkModeEnabled: boolean = false;
  
  // Feedback properties
  feedbackModalOpen: boolean = false;
  feedbackModalClosing: boolean = false;
  feedbackRating: number = 0;
  feedbackCategory: string = 'General';
  feedbackMessage: string = '';
  feedbackSubmitted: boolean = false;
  feedbackError: boolean = false;
  feedbackSubmitting: boolean = false;

  // CRM access management
  userCanAccessCrm: boolean = false;

  // Call Logs access management
  userCanAccessCallLogs: boolean = false;

  // Admin status and 2FA
  isAdminUser: boolean = false;
  show2FASetup: boolean = false;

  // Changelog modal
  showChangelogModal: boolean = false;
  changelogLoading: boolean = false;
  changelogVersions: Array<{
    id: string;
    version: string;
    title: string;
    release_date: string;
    items: Array<{ type: string; title?: string; description: string; image_url?: string }>;
  }> = [];
  changelogHasNew: boolean = false;
  private readonly CHANGELOG_SEEN_KEY = 'meetzy_last_seen_version';

  // Encuesta de producto (modal, se ve como mucho una vez por usuario)
  showSurveyModal: boolean = false;
  surveyUrl: SafeResourceUrl | null = null;
  private surveyMessageListener = (event: MessageEvent) => {
    console.log('🐛 [survey-debug] window message received', { origin: event.origin, data: event.data });
    if (event?.data?.type === 'meetzy-survey-completed') {
      console.log('🐛 [survey-debug] message matched meetzy-survey-completed, closing modal');
      this.closeSurveyModal();
    }
  };

  // Maps dashboard routes to their matching docs.meetzy.io page, so the
  // header "Docs" button jumps straight to the section that helps with
  // whatever the user is currently looking at instead of the docs homepage.
  private readonly docsRouteMap: Array<{ prefix: string; path: string }> = [
    { prefix: '/agents/voices', path: 'voices/choose-voice' },
    { prefix: '/advanced-editor', path: 'advanced-editor/introduction' },
    { prefix: '/playbooks', path: 'advanced-editor/introduction' },
    { prefix: '/agents', path: 'agents/manage-agents' },
    { prefix: '/phones', path: 'agents/phone-numbers' },
    { prefix: '/evaluation-trends', path: 'advanced-editor/evaluations' },
    { prefix: '/trends', path: 'calls/reporting' },
    { prefix: '/calls', path: 'calls/call-history' },
    { prefix: '/crm/segments', path: 'crm/segments' },
    { prefix: '/crm', path: 'crm/import-contacts' },
    { prefix: '/tickets', path: 'account/support-tickets' },
    { prefix: '/members', path: 'account/team-members' },
    { prefix: '/token-manager', path: 'account/api-tokens' },
    { prefix: '/account', path: 'account/permissions' },
  ];

  constructor(public auth: Auth, public router: Router, private authService: AuthService, private http: HttpClient, private apiService: ApiService, private permissionsService: PermissionsService, private twoFactorService: TwoFactorAuthService, private trackingService: TrackingService, private sanitizer: DomSanitizer) {
    window.addEventListener('message', this.surveyMessageListener);

    this.authService.isLoggedIn()
      .then((isLoggedIn: boolean) => {
        this.isLoggedIn = isLoggedIn;
      })
      .catch((error: any) => {
        console.log("error: ", error);
      })

    this.auth.onAuthStateChanged((user) => {
      if (user) {
        this.isLoggedIn = true;
        this.email = user.email ?? "";
        //this.partner = user.email?.split('@')[1];

        this.apiService.isPartner()
          .then( partner => {
            this.loading = false;
            this.partner=partner;
            
            // Check CRM access permissions
            this.checkCrmAccess();
            
            // Check Call Logs access permissions
            this.checkCallLogsAccess();
            
            // Check Admin status for 2FA
            this.checkAdminStatus();
            
            // Check for new changelog versions
            this.checkChangelog();

            // Check whether the product survey should be shown
            this.checkSurveyStatus();
          })
          .catch( error => {
            console.log('error partner: ', error);
          })

      } else {
        this.isLoggedIn = false;
      }
    });

    // Initialize dark mode from localStorage
    this.initializeDarkMode();
  }

  ngOnInit(): void {
    initFlowbite();
    this.router.events.pipe(
      filter(event => event instanceof NavigationEnd)
    ).subscribe((event: any) => {
      this.isLoginRoute = event.url === '/login';
      this.isGeneratorRoute = event.url.startsWith('/generator');
      // '/calls' now serves the new full-width calls-v2 page; '/calls-old'
      // is the classic boxed layout kept as a fallback, so it's deliberately
      // excluded here even though it starts with '/calls'. Same pattern for
      // the agents-v2 redesign: '/agents', '/agents-v2', '/playbooks' and the
      // home route ('') all serve the new full-width page now; '/agents-old'
      // keeps the classic boxed layout.
      const isAgentsV2Route = (event.url.startsWith('/agents') || event.url.startsWith('/playbooks') || event.url === '/' || event.url.startsWith('/?')) && !event.url.startsWith('/agents-old');
      this.isFullWidthRoute = event.url.startsWith('/trends') || event.url.startsWith('/stats') || event.url.startsWith('/user-activity') || event.url.startsWith('/agents/voices') || (event.url.startsWith('/calls') && !event.url.startsWith('/calls-old')) || isAgentsV2Route;
      this.isAdvancedEditorRoute = event.url.startsWith('/advanced-editor');
      // Cierra menú móvil y desplegables de escritorio al cambiar de ruta
      this.mobileMenuOpen = false;
      this.closeAllDesktopMenus();
    });

    // Add click event listener to close dropdown menus when clicking outside
    document.addEventListener('click', (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      
      // Check if click is outside the CRM menu
      if (!target.closest('#crm-menu') && !target.closest('.crm-menu-trigger') && !$('#crm-menu').hasClass('hidden')) {
        $('#crm-menu').addClass('hidden');
      }
      
      // Check if click is outside the Calls menu
      if (!target.closest('#calls-menu') && !target.closest('.calls-menu-trigger') && !$('#calls-menu').hasClass('hidden')) {
        $('#calls-menu').addClass('hidden');
      }
      
      // Check if click is outside the Marketing menu
      if (!target.closest('#marketing-menu') && !target.closest('.marketing-menu-trigger') && !$('#marketing-menu').hasClass('hidden')) {
        $('#marketing-menu').addClass('hidden');
      }
      
      // Check if click is outside the Agents menu
      if (!target.closest('#agents-menu') && !target.closest('.agents-menu-trigger') && !$('#agents-menu').hasClass('hidden')) {
        $('#agents-menu').addClass('hidden');
      }
    });
  }

  // Dark mode methods
  private initializeDarkMode(): void {
    // Check if user has a saved preference
    const savedTheme = localStorage.getItem('theme');
    if (savedTheme) {
      this._isDarkModeEnabled = savedTheme === 'dark';
    } else {
      // Default to system preference
      this._isDarkModeEnabled = window.matchMedia && window.matchMedia('(prefers-color-scheme: dark)').matches;
    }
    this.applyTheme();
  }

  private applyTheme(): void {
    if (this._isDarkModeEnabled) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }

  onThemeChange(isDark: boolean): void {
    this._isDarkModeEnabled = isDark;
    localStorage.setItem('theme', isDark ? 'dark' : 'light');
    this.applyTheme();
  }

  // Helper method to close all desktop menus
  closeAllDesktopMenus(): void {
    $('#crm-menu').addClass('hidden');
    $('#calls-menu').addClass('hidden');
    $('#marketing-menu').addClass('hidden');
    $('#agents-menu').addClass('hidden');
    $('#user-menu').addClass('hidden');
  }

  // Helper method to close all mobile menus
  closeAllMobileMenus(): void {
    $('#mobile-crm-menu').addClass('hidden');
    $('#mobile-calls-menu').addClass('hidden');
    $('#mobile-marketing-menu').addClass('hidden');
    $('#mobile-agents-menu').addClass('hidden');
  }

  gohome() {
    this.router.navigate(['https://www.meetzy.io']);
  }

  toggleMobileMenu() {
    this.mobileMenuOpen = !this.mobileMenuOpen;
  }

  toggleUserMenu() {
    $("#user-menu").toggleClass("hidden");
  }

  // Cierra los demás y abre este; si ya estaba abierto solo lo cierra (antes
  // closeAll + toggleClass lo reabría y se quedaba abierto tras navegar).
  private toggleExclusiveMenu(id: string, mobile: boolean): void {
    const menu = $(`#${id}`);
    const wasOpen = !menu.hasClass('hidden');
    if (mobile) this.closeAllMobileMenus(); else this.closeAllDesktopMenus();
    if (!wasOpen) menu.removeClass('hidden');
  }

  private isMobileMenuEvent(event?: MouseEvent): boolean {
    return !!(event?.currentTarget && (event.currentTarget as HTMLElement).closest('#mobile-menu'));
  }

  toggleCallsMenu(event?: MouseEvent) {
    event?.stopPropagation();
    const mobile = this.isMobileMenuEvent(event);
    this.toggleExclusiveMenu(mobile ? 'mobile-calls-menu' : 'calls-menu', mobile);
  }

  toggleMarketingMenu(event?: MouseEvent) {
    event?.stopPropagation();
    const mobile = this.isMobileMenuEvent(event);
    this.toggleExclusiveMenu(mobile ? 'mobile-marketing-menu' : 'marketing-menu', mobile);
  }

  toggleCrmMenu(event?: MouseEvent) {
    event?.stopPropagation();
    const mobile = this.isMobileMenuEvent(event);
    this.toggleExclusiveMenu(mobile ? 'mobile-crm-menu' : 'crm-menu', mobile);
  }

  toggleAgentsMenu(event?: MouseEvent) {
    event?.stopPropagation();
    const mobile = this.isMobileMenuEvent(event);
    this.toggleExclusiveMenu(mobile ? 'mobile-agents-menu' : 'agents-menu', mobile);
  }

  logout() {
    this.auth.signOut();
    //redirect to login page
    this.router.navigate(['/login']);
  }

  // Check if dark mode should be applied
  isDarkMode(): boolean {
    return this._isDarkModeEnabled;
  }

  // Get current dark mode state for the toggle component
  get darkModeEnabled(): boolean {
    return this._isDarkModeEnabled;
  }

  // Check if we should show the theme toggle
  shouldShowThemeToggle(): boolean {
    return true;
  }

  // Feedback methods
  toggleFeedbackModal() {
    if (!this.feedbackModalOpen) {
      // Opening the modal
      this.feedbackModalOpen = true;
      this.feedbackModalClosing = false;
      
      // Reset form when opening
      this.feedbackRating = 0;
      this.feedbackCategory = 'General';
      this.feedbackMessage = '';
      this.feedbackSubmitted = false;
      this.feedbackError = false;
    } else {
      // Closing with animation
      this.feedbackModalClosing = true;
      
      // Set a timeout to actually remove the modal from the DOM
      // after the animation completes
      setTimeout(() => {
        this.feedbackModalOpen = false;
        this.feedbackModalClosing = false;
      }, 300); // Match this to the duration in the CSS transition
    }
  }

  setFeedbackRating(rating: number) {
    this.feedbackRating = rating;
  }

  submitFeedback(event: Event) {
    event.preventDefault();
    
    if (!this.feedbackMessage || this.feedbackSubmitting) {
      return;
    }
    
    // Set loading state
    this.feedbackSubmitting = true;
    
    this.apiService.submitFeedback({
      message: this.feedbackMessage,
      rating: this.feedbackRating,
      category: this.feedbackCategory
    })
    .then(response => {
      this.feedbackSubmitting = false;
      this.feedbackSubmitted = true;
      this.feedbackError = false;
      
      // Close modal after showing success for 2 seconds
      setTimeout(() => {
        // Start closing animation
        this.feedbackModalClosing = true;
        
        // Remove from DOM after animation completes
        setTimeout(() => {
          this.feedbackModalOpen = false;
          this.feedbackModalClosing = false;
        }, 300);
      }, 2000);
    })
    .catch(error => {
      this.feedbackSubmitting = false;
      console.error('Error submitting feedback:', error);
      this.feedbackError = true;
      this.feedbackSubmitted = false;
    });
  }

  /**
   * Check if the current user can access CRM
   */
  private async checkCrmAccess(): Promise<void> {
    try {
      this.userCanAccessCrm = await this.permissionsService.canAccessCrm();
      console.log('🔐 AppComponent: CRM access check result:', this.userCanAccessCrm);
    } catch (error) {
      console.error('🔐 AppComponent: Error checking CRM access:', error);
      this.userCanAccessCrm = false;
    }
  }

  /**
   * Check if the current user can access Call Logs
   */
  private async checkCallLogsAccess(): Promise<void> {
    try {
      this.userCanAccessCallLogs = await this.apiService.hasCallLogsAccess();
      console.log('🔐 AppComponent: Call Logs access check result:', this.userCanAccessCallLogs);
    } catch (error) {
      console.error('🔐 AppComponent: Error checking Call Logs access:', error);
      this.userCanAccessCallLogs = false;
    }
  }

  private async checkAdminStatus(): Promise<void> {
    try {
      this.isAdminUser = await this.apiService.isAdmin();
      console.log('🔐 AppComponent: Admin status check result:', this.isAdminUser);
    } catch (error) {
      console.error('🔐 AppComponent: Error checking admin status:', error);
      this.isAdminUser = false;
    }
  }

  open2FASetup(): void {
    this.show2FASetup = true;
  }

  close2FASetup(): void {
    this.show2FASetup = false;
  }

  private async checkChangelog(): Promise<void> {
    try {
      const result = await this.apiService.getLatestVersion();
      if (result.success && result.version) {
        const lastSeen = localStorage.getItem(this.CHANGELOG_SEEN_KEY);
        if (!lastSeen || lastSeen !== result.version.version) {
          this.changelogHasNew = true;
          this.loadChangelog();
        }
      }
    } catch (error) {
      console.error('Error checking changelog:', error);
    }
  }

  private async loadChangelog(): Promise<void> {
    this.changelogLoading = true;
    try {
      const result = await this.apiService.getChangelog();
      if (result.success) {
        this.changelogVersions = result.versions;
        if (this.changelogVersions.length > 0) {
          this.showChangelogModal = true;
        }
      }
    } catch (error) {
      console.error('Error loading changelog:', error);
    } finally {
      this.changelogLoading = false;
    }
  }

  // Docs URL that jumps to the section matching the page the user is on,
  // falling back to the docs homepage for routes with no dedicated page.
  getDocsUrl(): string {
    const url = this.router.url;
    const match = this.docsRouteMap.find(entry => url.startsWith(entry.prefix));
    return match ? `https://docs.meetzy.io/${match.path}` : 'https://docs.meetzy.io';
  }

  closeChangelogModal(): void {
    this.showChangelogModal = false;
    if (this.changelogVersions.length > 0) {
      localStorage.setItem(this.CHANGELOG_SEEN_KEY, this.changelogVersions[0].version);
      this.changelogHasNew = false;
    }
  }

  openChangelogModal(): void {
    if (this.changelogVersions.length === 0) {
      this.loadChangelog();
    }
    this.showChangelogModal = true;
  }

  private async checkSurveyStatus(): Promise<void> {
    try {
      const dismissed = await this.apiService.getSurveyDismissed();
      console.log('🐛 [survey-debug] checkSurveyStatus', { dismissed, email: this.email });
      if (!dismissed && this.email) {
        const respondentId = btoa(this.email);
        this.surveyUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
          `https://meetzy.es/encuesta?id=${respondentId}`
        );
        this.showSurveyModal = true;
        console.log('🐛 [survey-debug] showSurveyModal set to true', { respondentId, url: `https://meetzy.es/encuesta?id=${respondentId}` });
      }
    } catch (error) {
      console.error('🐛 [survey-debug] Error checking survey status:', error);
    }
  }

  closeSurveyModal(): void {
    console.log('🐛 [survey-debug] closeSurveyModal called', { showSurveyModal: this.showSurveyModal, stack: new Error().stack });
    if (!this.showSurveyModal) return;
    this.showSurveyModal = false;
    this.apiService.setSurveyDismissed().catch(error => {
      console.error('🐛 [survey-debug] Error dismissing survey:', error);
    });
  }

  getChangelogItemIcon(type: string): string {
    switch (type) {
      case 'feature': return 'text-emerald-500';
      case 'improvement': return 'text-blue-500';
      case 'bugfix': return 'text-amber-500';
      default: return 'text-gray-500';
    }
  }

  getChangelogItemLabel(type: string): string {
    switch (type) {
      case 'feature': return 'New';
      case 'improvement': return 'Improved';
      case 'bugfix': return 'Fixed';
      default: return 'Update';
    }
  }

  getChangelogItemBadgeClass(type: string): string {
    switch (type) {
      case 'feature': return 'bg-emerald-100 text-emerald-700 ring-emerald-600/20';
      case 'improvement': return 'bg-blue-100 text-blue-700 ring-blue-600/20';
      case 'bugfix': return 'bg-amber-100 text-amber-700 ring-amber-600/20';
      default: return 'bg-gray-100 text-gray-700 ring-gray-600/20';
    }
  }

  getChangelogItemBadgeClassDark(type: string): string {
    switch (type) {
      case 'feature': return 'bg-emerald-900/50 text-emerald-300 ring-emerald-500/30';
      case 'improvement': return 'bg-blue-900/50 text-blue-300 ring-blue-500/30';
      case 'bugfix': return 'bg-amber-900/50 text-amber-300 ring-amber-500/30';
      default: return 'bg-gray-700 text-gray-300 ring-gray-500/30';
    }
  }

  formatChangelogDate(date: string): string {
    try {
      return new Date(date).toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    } catch {
      return date;
    }
  }
}

